import {useTranslation} from "react-i18next";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import {TENANT_FORM_FEATURE_FLAGS} from "../../constants/featureFlags";
import {featureFlagLabel} from "../../constants/featureFlagLabels";
import type {GlobalFeatureFlags, SmsProvider, TenantFeatureConfig} from "../../types/tenant";
import {effectiveFlag, storedFlagValue} from "../../utils/featureFlagResolution";
import {colors} from "../../theme/tokens";
import {TernaryFeatureFlag, type TernaryFlagValue} from "./TernaryFeatureFlag";

type TenantFeatureFlagsEditorProps = {
    value: TenantFeatureConfig;
    onChange: (config: TenantFeatureConfig) => void;
    /** When loaded, inherited flags show the global default they resolve to. */
    globalFlags?: GlobalFeatureFlags | null;
    /** Flags the backend sets regardless of the form, shown read-only with the value that applies. */
    lockedFlags?: Partial<Record<string, {value: TernaryFlagValue; hint: string}>>;
};

/** Tri-state overrides for the tenant form's flags, in two columns; `null` stores inheritance. */
export function TenantFeatureFlagsEditor({value, onChange, globalFlags, lockedFlags}: TenantFeatureFlagsEditorProps) {
    const {t} = useTranslation();

    const inheritedHint = (flag: string): string | null => {
        if (!globalFlags) return null;
        const effective = effectiveFlag(storedFlagValue(value, flag), globalFlags[flag]);
        if (effective.source === "override") return null;
        if (effective.source === "unset") return t("FEATURE_GLOBAL_UNSET");
        return effective.value ? t("FEATURE_GLOBAL_ON") : t("FEATURE_GLOBAL_OFF");
    };

    return (
            <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", md: "1fr 1fr"}, columnGap: 4}}>
                {TENANT_FORM_FEATURE_FLAGS.map((flag) => {
                    const label = featureFlagLabel(t, flag);
                    const locked = lockedFlags?.[flag];
                    const hint = locked ? locked.hint : inheritedHint(flag);
                    return (
                            <Box
                                    key={flag}
                                    sx={{display: "flex", alignItems: "center", gap: 1, minHeight: 33, borderBottom: `1px solid ${colors.rowDivider}`}}
                            >
                                <Box component="span" sx={{flex: 1, fontSize: "13px", color: colors.ink}}>
                                    {label}
                                    {hint && (
                                            <Box component="span" sx={{ml: 1, fontSize: "11px", color: colors.faint}}>
                                                {hint}
                                            </Box>
                                    )}
                                </Box>
                                <TernaryFeatureFlag
                                        value={locked ? locked.value : storedFlagValue(value, flag)}
                                        onChange={(next) => onChange({...value, [flag]: next})}
                                        aria-label={label}
                                        disabled={Boolean(locked)}
                                />
                            </Box>
                    );
                })}
            </Box>
    );
}

export function SmsProviderSelect({value, onChange, id}: {value: TenantFeatureConfig; onChange: (config: TenantFeatureConfig) => void; id: string}) {
    const {t} = useTranslation();
    const provider: SmsProvider = value.smsProvider === "IBASIS" ? "IBASIS" : "AWS_SNS";
    return (
            <FormControl size="small" sx={{minWidth: 140, flexDirection: "row", alignItems: "center", gap: 1}}>
                <InputLabel id={`${id}-label`} sx={{mb: 0}}>{t("FEATURE_SMS_PROVIDER")}</InputLabel>
                <Select
                        labelId={`${id}-label`}
                        id={id}
                        value={provider}
                        label={t("FEATURE_SMS_PROVIDER")}
                        onChange={(e) => onChange({...value, smsProvider: e.target.value as SmsProvider})}
                        sx={{minWidth: 120}}
                >
                    <MenuItem value="AWS_SNS">AWS SNS</MenuItem>
                    <MenuItem value="IBASIS">IBASIS</MenuItem>
                </Select>
            </FormControl>
    );
}
